import cx from 'classnames'
import { useContext } from 'react'

import {
  type SanityProductOption,
  type SanityProductOptionSetting,
  type SanityProductVariantFragment,
} from '@data/sanity/queries/types/product'
import { hasObject } from '@lib/helpers'
import { getVariantFromOptions } from '@lib/product'
import { type OptionValue } from '@lib/product-card'
import { StringsContext } from '@lib/strings-context'

import {
  ButtonSize,
  ButtonVariant,
  getButtonStyles,
} from '@components/buttons/button'
import RadioItem from '@components/radio-item'
import Swatch from '@components/swatch'

interface ProductOptionItemProps {
  value: string
  option: SanityProductOption
  optionSettings: SanityProductOptionSetting[]
  variants: SanityProductVariantFragment[]
  activeVariant: SanityProductVariantFragment
  strictMatch: boolean
  isActive?: boolean
  onPreview?: (variant?: SanityProductVariantFragment) => void
}

const ProductOptionItem = ({
  value,
  option,
  optionSettings,
  variants,
  activeVariant,
  strictMatch,
  isActive,
  onPreview,
}: ProductOptionItemProps) => {
  const strings = useContext(StringsContext)

  const currentOption: OptionValue[] = [
    {
      name: option.name,
      value,
    },
  ]
  const allOptions: OptionValue[] = [
    ...currentOption,
    ...activeVariant.options.filter(
      (variantOption) =>
        variantOption.position !== option.position ||
        variantOption.name !== option.name,
    ),
  ]

  const optionSetting = optionSettings.find((optionSetting) => {
    const optionParts = optionSetting.forOption?.split(':')

    return optionParts?.[0] === option.name && optionParts?.[1] === value
  })
  const optionColor = optionSetting?.color

  const hasVariants = variants.find((variant) =>
    variant.options.every((variantOption) =>
      hasObject(allOptions, variantOption),
    ),
  )
  const inStock = variants.find((variant) => {
    if (!variant.inStock) {
      return false
    }

    if (strictMatch) {
      return variant.options.every((variantOption) =>
        hasObject(allOptions, variantOption),
      )
    }

    return variant.options.some((variantOption) =>
      hasObject(currentOption, variantOption),
    )
  })

  const onMouseEnter = () => {
    const newVariant = getVariantFromOptions(
      variants,
      activeVariant.options,
      option.name,
      value,
    )

    if (onPreview && newVariant) {
      onPreview(newVariant)
    }
  }
  const onMouseLeave = () => {
    if (onPreview) {
      onPreview()
    }
  }

  return (
    <RadioItem
      value={value}
      className={cx('mt-1 mb-1 ml-2', {
        'p-0 bg-transparent': !!optionColor,
      })}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {!!optionColor && (
        <Swatch
          label={strings.productColorOptionLabel
            .replace(/{value}/gi, value)
            .replace(/{name}/gi, option.name.toLowerCase())}
          color={optionColor}
          isActive={isActive}
          isCrossed={!inStock && hasVariants && !isActive}
        />
      )}

      {!optionColor && (
        <div
          className={cx(
            getButtonStyles({
              variant: ButtonVariant.OUTLINED,
              size: ButtonSize.SMALL,
              isActive,
            }),
            {
              'opacity-30': !inStock && hasVariants && !isActive,
            },
          )}
        >
          {value}
        </div>
      )}
    </RadioItem>
  )
}

export default ProductOptionItem
